/* eslint-disable max-len */
import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import fileDownload from 'js-file-download';
import {
  Typography,
  Card,
  CardContent,
  Chip,
  Icon,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  FormControlLabel,
  FormControl,
  TextField,
  Checkbox,
} from '@mui/material';

import Check from '@mui/icons-material/Check';

import { subDays, startOfDay, endOfDay } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';

import { withStyles } from '@mui/styles';
import { GetApp} from '@mui/icons-material';

import { LocationsUtil, withRouter} from 'utils';
import {
  DynamicTable,
  FindProviderDialog,
  FindUserDialog,
  SmallIconTextButton,
  LoadingView,
} from 'components';
import { FindContactDialog } from 'contacts';
import { FindLocationDialog } from 'locations';
import { queryProviderComments, queryProviderCommentsDocuments, queryProviderCommentsProviders, queryProviderCommentsContacts, queryProviderCommentsLocations } from 'store/actions/providerCommentsActions';
import {
  API_TAG_NAMESPACES_LIST_BY_CLIENT,
  listTagNamespacesByClient,
} from 'store/actions/tagNamespacesActions';
import axiosInstance from 'utils/axiosUtil';
import {
  CsvUtil,
  StoreUtil,
  DateUtil,
  EncounterTypeUtil,
} from 'doctivity-shared/utils';

import {
  queryClients,
} from 'store/actions/clientsActions';




/* -------------------------------------------------------------------------- */
/*  HELPER  –  merge comments with their children and (auto)-filter           */
/* -------------------------------------------------------------------------- */
export function mergeCommentsDocsAndProviders(
  comments,
  docs,
  provs,
  contacts,
  locations
  /* the three “…required” flags are no longer needed                       */
) {
  /* ---------------------------------------------------------------------- */
  /* 1.  Find out which filter chips are currently active (if any)           */
  /* ---------------------------------------------------------------------- */
  let needProvs = false;
  let needContacts = false;
  let needLocations = false;

  try {
    const filterObj = JSON.parse(
      sessionStorage.getItem('activity-note-filters') || '{}'
    );

    needProvs     = Array.isArray(filterObj.providers) && filterObj.providers.length  > 0;
    needContacts  = Array.isArray(filterObj.contacts)  && filterObj.contacts.length   > 0;
    needLocations = Array.isArray(filterObj.locations) && filterObj.locations.length  > 0;
  } catch (_) {
    /* ignore malformed JSON – treat as “no filters active” */
  }

  /* ---------------------------------------------------------------------- */
  /* 2.  Normalise {rows:[…]} wrappers coming from Sequelize tables          */
  /* ---------------------------------------------------------------------- */
  const documentsList = Array.isArray(docs)      ? docs      : docs?.rows      || [];
  const providersList = Array.isArray(provs)     ? provs     : provs?.rows     || [];
  const contactsList  = Array.isArray(contacts)  ? contacts  : contacts?.rows  || [];
  const locationsList = Array.isArray(locations) ? locations : locations?.rows || [];
  const commentsList  = Array.isArray(comments)  ? comments  : comments?.rows  || [];

  /* ---------------------------------------------------------------------- */
  /* 3.  Build quick lookup maps keyed by the comment-id                     */
  /* ---------------------------------------------------------------------- */
  const docMap      = Object.create(null);
  const providerMap = Object.create(null);
  const contactMap  = Object.create(null);
  const locationMap = Object.create(null);

  for (const d of documentsList)
    if (d?.id != null)
      docMap[d.id] = Array.isArray(d.documents) ? d.documents : [d];

  for (const p of providersList)
    if (p?.id != null)
      providerMap[p.id] = Array.isArray(p.providers) ? p.providers : [p];

  for (const c of contactsList)
    if (c?.id != null)
      contactMap[c.id] = Array.isArray(c.contacts) ? c.contacts : [c];

  for (const l of locationsList)
    if (l?.id != null)
      locationMap[l.id] = Array.isArray(l.locations) ? l.locations : [l];

  /* ---------------------------------------------------------------------- */
  /* 4.  Merge – and, if required, drop the rows that don’t match            */
  /* ---------------------------------------------------------------------- */
  const mergedRows = [];

  for (const c of commentsList) {
    const merged = {
      ...c,
      documents: docMap[c.id]      || [],
      providers: providerMap[c.id] || [],
      contacts : contactMap[c.id]  || [],
      locations: locationMap[c.id] || [],
    };

    if (
      (needProvs     && merged.providers.length  === 0) ||
      (needContacts  && merged.contacts.length   === 0) ||
      (needLocations && merged.locations.length  === 0)
    ) {
      continue;                   // filtered out – does not satisfy criteria
    }

    mergedRows.push(merged);
  }

  return mergedRows;
}

const styles = (theme) => ({
  container: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  filters: {
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  leftIcon: {
    marginRight: theme.spacing(),
  },
  lineBreaks: {
    whiteSpace: 'pre',
  },
  paddedRow: {
    paddingTop: '5px',
    paddingBottom: '5px',
    whiteSpace: 'pre',
  },
  encounterIcon: {
    fontSize: 16,
    color: '#333333',
    marginLeft: 2,
  },
  attachIcon: {
    fontSize: 16,
    color: '#333333',
    marginLeft: 2,
    transform: 'rotate(45deg)'
  },
  header: {
    padding: theme.spacing(3),
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  headerTitle: {
    fontSize: 24,
    paddingRight: 30,
  },
  headerContainer: {
    marginBottom: theme.spacing(3),
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  spacer: {
    flex: 1,
  },
  pdfButton: {
    flex: 1,
  },
  pdfIcon: {
    fontSize: 20,
    fontWeight: 200,
  },
  filterSpace: {
    width: 4,
  },
  rangeControl: {
    minWidth: 90,
  },
  tabs: {
    paddingLeft: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  filterColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
});

// this controls which columns are displayed and how they are looked up in data
const columns = [
 
  {
    label: '',
    key: 'encounter_type_id',
    format: (id, row, rowClasses) => (
      <Icon className={rowClasses.encounterIcon}>
        {EncounterTypeUtil.getIcon(id)}
      </Icon>
    ),
    style: { width: 20 },
    hideForMobile: false,
  },
  {
    label: 'Date',
    key: 'comment_date',
    type: 'date',
    format: DateUtil.formatDateForUser,
    style: { width: 60 },
    hideForMobile: false,
  },
//  {
  //  label: 'Locations',
    //key: 'locations.address1',
    //format: (_id, { locations }) =>
      //locations && LocationsUtil.formatFullNameAndAddress(locations, false),
    //style: { width: 200 },
    //hideForMobile: true,
  //},
  {
    label: 'Locations',
    key: 'locations',
    format: (locations, row, rowClasses) => {
      const names = [];
      if (locations && locations.length > 0) {
        let counter = 0
        locations.every((l) => {
          counter++;
          if (counter > 2) {
            names.push(`(+${locations.length-2} more)`);
            return false;
          } else {
          //names.push(`${l.name}, ${l.address1}`);
          names.push(LocationsUtil.formatFullNameAndAddress(l, false))
            return true;
          }
          
        });
      }
      return <div className={rowClasses.paddedRow}>{names.join('\n')}</div>;
    },
    // fulltext: true,
    // filterFields: ['providers.last_name', 'providers.first_name'],
    sortable: false,
    style: { width: 180 },
    hideForMobile: true,
  },

  {
    label: 'Providers',
    key: 'providers',
    format: (providers, row, rowClasses) => {
      const names = [];
      if (providers && providers.length > 0) {
        let counter = 0
        providers.every((p) => {
          counter++;
          if (counter > 5) {
            names.push(`(+${providers.length-5} more)`);
            return false;
          } else {
            names.push(`${p.first_name} ${p.last_name}${p.credential ? ', ' +  p.credential : ''}`);
            return true;
          }
          
        });
      }
      return <div className={rowClasses.paddedRow}>{names.join('\n')}</div>;
    },
    // fulltext: true,
    // filterFields: ['providers.last_name', 'providers.first_name'],
    sortable: false,
    style: { width: 180 },
    hideForMobile: true,
  },
  {
    label: 'Contacts',
    key: 'contacts',
    format: (contacts, row, rowClasses) => {
      const names = [];
      if (row.contacts && row.contacts.length > 0) {
        let counter = 0
        contacts.every((c) => {
          counter++;
          if (counter > 5) {
            names.push(`(+${contacts.length-5} more)`);
            return false;
          } else {
            names.push(`${c.last_name}, ${c.first_name}`);
            return true;
          }
        });
      }
      return <div className={rowClasses.lineBreaks}>{names.join('\n')}</div>;
    },
    // fulltext: true,
    // filterFields: ['contacts.last_name', 'contacts.first_name'],
    sortable: false,
    style: { width: 180 },
    hideForMobile: true,
  },
  {
    label: 'User',
    key: 'user',
    format: (user) => user && `${user.last_name}, ${user.first_name}`,
    style: { width: 100 },
    hideForMobile: false,
  },
  {
    label: 'Note',
    key: 'body',
    format: (body) => body && body.replace(/(.{225}).*/, '$1 ...').substring(0,228),
    style: { maxWidth: 200, width: 200, whiteSpace: 'nowrap', wordWrap: 'nowrap'},
    hideForMobile: true,
  },
  {
    label: '',
    key: 'documents',
    format: (id, row, rowClasses) => (
      <Icon className={rowClasses.attachIcon}>
             {row.documents.length ? 'attach_file' : '' }
      </Icon>
    ),
    style: { width: 20 },
    hideForMobile: false,
  },
];
const isValidDate = (d) => d instanceof Date && !isNaN(d);
const safeISOString = (d, fallback = new Date(0)) =>
+  isValidDate(d) ? d.toISOString() : fallback.toISOString();
const FILTER_KEYS = [
  'tags',
  'documents',
  'users',
  'contacts',
  'providers',
  'locations',
  'encounterTypes',
  'needsResolved',
];

class ProviderCommentsPage extends React.Component {
  constructor(props) {
    super(props);
    const startDate = new Date(
      sessionStorage.getItem('startDate') ?? undefined,
    );
    const endDate = new Date(sessionStorage.getItem('endDate') ?? undefined);
    let filters = JSON.parse(
      sessionStorage.getItem('activity-note-filters') ??
        '{"tags": [],"users":[],"contacts":[],"providers":[],"locations":[],"encounterTypes":[]}',
    );
    let filterClient = parseInt(
      sessionStorage.getItem('activity-note-client') ?? '-1',
      10,
    );

    if (filterClient && filterClient !== props.app.selectedClient) {
      sessionStorage.removeItem('activity-note-filters');
      sessionStorage.removeItem('activity-note-client');
      filters = {};
    }
    this.state = {
      locations: filters.locations ?? [],
      providers: filters.providers ?? [],
      users: filters?.hasChanged ? filters.users : null,//[props.user],
      contacts: filters.contacts ?? [],
      tags: filters.tags ?? [],
      documents: filters.documents ?? [],
      encounterTypes: filters.encounterTypes ?? [],
      needsResolved: filters.needsResolved ?? false,
      findLocationOpen: false,
      findProviderOpen: false,
      findContactOpen: false,
      findUserOpen: false,
      tagsOpen: false,
      tagsAnchorEl: null,
      tagsNamespace: null,
      findEncounterTypeOpen: false,
      encounterTypeAnchorEl: null,
      startDate: startOfDay(
        isValidDate(startDate) ? startDate : subDays(new Date(), 30),
      ),
      endDate: endOfDay(isValidDate(endDate) ? endDate : new Date()),
      query: {
        distinct: true, // this keeps counts accurate with multiple rows
        where: {},
        include: [
          {
            association: 'user',
            attributes: ['id', 'first_name', 'last_name'],
          },
          {
            association: 'providers',
            attributes: ['id', 'first_name', 'last_name', 'middle_name', 'credential'],
          },
          {
            association: 'contacts',
            attributes: ['id', 'first_name', 'last_name','company'],
          },
          {
            association: 'locations',
            attributes: ['id', 'name', 'address1', 'address2', 'city', 'state', 'postal_code'],
          },          
          {
            association: 'tags',
            attributes: ['id', 'name', 'namespace_id'],
            include: [
              { association: 'namespace', attributes: ['id', 'name'] },
            ],  
          },
          {
            association: 'documents',
            attributes: ['id'],
          }
        ],
        // order set in buildQuery
        limit: 25,
        offset: 0,
      },
    };

    columns[0].onFilterClick = () => {
      this.onAdd();
    };
  }

  componentDidMount() {
    this.fetchData();

   /* listen once for externally-triggered refresh */
   window.addEventListener('refresh-activity-notes', this.fetchData);

    sessionStorage.setItem(
      'activity-note-client',
      this.props.app.selectedClient,
    );
    if (
      StoreUtil.needsLoadLongCache(this.props.tagNamespaces) ||
      this.props.tagNamespaces.clientId !== this.props.app.selectedClient
    ) {
      this.fetchTagNamespaces();
    }
  }

  componentWillUnmount() {
   window.removeEventListener('refresh-activity-notes', this.fetchData);
  }

  componentDidUpdate(prevProps, prevState) {
    // anytime the client changes
    if (this.props.app.selectedClient !== prevProps.app.selectedClient) {
      sessionStorage.removeItem('activity-note-filters');
      sessionStorage.removeItem('activity-note-client');
      this.fetchData();
    } else if (this.props.editComment !== prevProps.editComment) {
      // is the list data older than the edited data?
      if (
        StoreUtil.hasSavedSinceLoad(this.props.editComment, this.props.comments)
      ) {
        this.fetchData();
      }
    } else if (StoreUtil.needsLoadNoCache(this.props.comments)) {
     // this.fetchData();
    }

    if (
      StoreUtil.needsLoadLongCache(this.props.tagNamespaces) ||
      this.props.tagNamespaces.clientId !== this.props.app.selectedClient
    ) {
      this.fetchTagNamespaces();
    }
    if (this.state !== prevState) {
      for (const key of FILTER_KEYS) {
        if (this.state[key] !== prevState[key] | key==='tags') {
          const filters = FILTER_KEYS.reduce((acc, k) => {
            acc[k] = this.state[k];
            return acc;
          }, {});
          sessionStorage.setItem(
            'activity-note-filters',
            JSON.stringify({ hasChanged: true, ...filters }),
          );
          break;
        }
      }
    }
  }

  buildQuery(allTags) {
    const { app } = this.props;

    const {
      query,
      //locations,
      //providers,
      //contacts,
      encounterTypes, 
      needsResolved,
    } = this.state;

    const users = this.getUsers();

    // restrict to current selected client
    query.where.client_id = app.selectedClient;
    //const location = query.include.find((i) => i.association === 'location');
    //if (location && locations && locations.length > 0) {
      //location.where = { id: locations.map((l) => l.id) };
    //} else if (location?.where) {
      //delete location.where;
    //}

    /* --- this needs to move to the individual quries
    if (locations && locations.length > 0) {
      query.include[3].where = {
        id: locations.map((l) => l.id),
      };
    } else if (query.include[3].where) {
      delete query.include[3].where;
    }
    if (providers && providers.length > 0) {
      query.include[1].where = {
        id: providers.map((p) => p.id),
      };
    } else if (query.include[1].where) {
      delete query.include[1].where;
    }
    if (contacts && contacts.length > 0) {
      query.include[2].where = {
        id: contacts.map((c) => c.id),
      };
    } else if (query.include[2].where) {
      delete query.include[2].where;
    }
    */
    const tagInclude = query.include.find((i) => i.association === 'tags');
    if (this.state.tags && this.state.tags.length > 0) {

      if (!allTags) {

        tagInclude.where = { $or: [] };
        this.state.tags.forEach((namespace) => {
          tagInclude.where.$or.push({
            namespace_id: namespace.id,
            id: namespace.tags.map((t) => t.id),
          });
        });
      } else {
        query.limit = 100000;
        delete tagInclude.where;
      }
    } else if (tagInclude.where) {
      delete tagInclude.where;
    }

    // Remove providers
    query.include = query.include.filter((i) => i.association !== 'providers');
    // Remove contacts
    query.include = query.include.filter((i) => i.association !== 'contacts');
        // Remove locations
    query.include = query.include.filter((i) => i.association !== 'locations');
    // Remove documents
    query.include = query.include.filter((i) => i.association !== 'documents');

    if (encounterTypes && encounterTypes.length > 0) {
      query.where.encounter_type_id = encounterTypes.map((et) => et.id);
    } else if (query.where.encounter_type_id) {
      delete query.where.encounter_type_id;
    }
    if (users && users.length > 0) {
      query.include[0].where = {
        id: users.map((u) => u.id),
      };
    } else if (query.include[0].where) {
      delete query.include[0].where;
    }
    if (needsResolved) {
      query.where.needs_followup = true;
      query.where.$or = [{ is_resolved: false }, { is_resolved: null }];
      if (!query.order) {
        query.order = [['due_date', 'DESC']];
      } else if (query.order[0][0] === 'comment_date') {
        query.order[0][0] = 'due_date';
      }
    } else {
      delete query.where.needs_followup;
      delete query.where.$or;
      if (!query.order) {
        query.order = [['comment_date', 'DESC']];
      } else if (query.order[0][0] === 'due_date') {
        query.order[0][0] = 'comment_date';
      }
    }
    query.where.comment_date = {
      $gte: this.state.startDate.toISOString(),
      $lte: this.state.endDate.toISOString(),
    };
    
    // never call .toISOString on a bad Date
    query.where.comment_date = {
      $gte: safeISOString(this.state.startDate),
      $lte: safeISOString(this.state.endDate, new Date()),
    };
    
    return query;
  }

  /**
 * Build a query that fetches “docs only”.
 *   – Does NOT touch or read this.state.query at all.
 *   – Returns a completely new query object each time it is called.
 */
  buildDocsOnlyQuery() {
    const { app } = this.props;
    const { needsResolved, startDate, endDate } = this.state;

    const query =  {
      distinct: true, // this keeps counts accurate with multiple rows
      where: {},
      include: [
        {
          association: 'documents',
          attributes: ['id'],
        }
      ],
      // order set in buildQuery
      limit: 500,
      offset: 0,
    };
    
    query.where.client_id = app.selectedClient;        // restrict to client

    // Date range
    query.where.comment_date = {
      $gte: startDate.toISOString(),
      $lte: endDate.toISOString()
    };
   

    if (needsResolved) {
      query.where.needs_followup = true;
      query.where.$or = [{ is_resolved: false }, { is_resolved: null }];
      query.order = [['due_date', 'DESC']];
    } else {
      // no follow-up filter
      query.order = [['comment_date', 'DESC']];
    }

    
    const documentInclude = { association: 'documents' };

    query.include.push(documentInclude);
    

    // never call .toISOString on a bad Date
    query.where.comment_date = {
      $gte: safeISOString(this.state.startDate),
      $lte: safeISOString(this.state.endDate, new Date()),
    };
    

    return query;
  }

    /**
 * Build a query that fetches “providers only”.
 *   – Does NOT touch or read this.state.query at all.
 *   – Returns a completely new query object each time it is called.
 */
  buildProvidersOnlyQuery() {
    const { app } = this.props;
    const { needsResolved, startDate, endDate, providers } = this.state;

    const query =  {
      distinct: true, // this keeps counts accurate with multiple rows
      where: {},
      include: [
        {
          association: 'providers',
           attributes: ['id', 'first_name', 'last_name', 'middle_name', 'credential'],
        }
      ],
      // order set in buildQuery
      limit: 500,
      offset: 0,
    };

    if (providers && providers.length > 0) {
      query.include[0].where = {
        id: providers.map((p) => p.id),
      }
    } else if (query.include[0].where) {
      delete query.include[0].where;
    }

    query.where.client_id = app.selectedClient;        // restrict to client

    // Date range
    query.where.comment_date = {
      $gte: startDate.toISOString(),
      $lte: endDate.toISOString()
    };
   

    if (needsResolved) {
      query.where.needs_followup = true;
      query.where.$or = [{ is_resolved: false }, { is_resolved: null }];
      query.order = [['due_date', 'DESC']];
    } else {
      // no follow-up filter
      query.order = [['comment_date', 'DESC']];
    }

    
    const providersInclude = { association: 'providers' };

    query.include.push(providersInclude);


    // never call .toISOString on a bad Date
    query.where.comment_date = {
      $gte: safeISOString(this.state.startDate),
      $lte: safeISOString(this.state.endDate, new Date()),
    };
    

    return query;
  }

    /**
 * Build a query that fetches “providers only”.
 *   – Does NOT touch or read this.state.query at all.
 *   – Returns a completely new query object each time it is called.
 */
  buildContactsOnlyQuery() {
    const { app } = this.props;
    const { needsResolved, startDate, endDate, contacts } = this.state;

    const query =  {
      distinct: true, // this keeps counts accurate with multiple rows
      where: {},
      include: [
        {
          association: 'contacts',
          attributes: ['id', 'first_name', 'last_name','company'],
        }
      ],
      // order set in buildQuery
      limit: 500,
      offset: 0,
    };
    
    if (contacts && contacts.length > 0) {
      query.include[0].where = {
        id: contacts.map((c) => c.id),
      }
    } else if (query.include[0].where) {
      delete query.include[0].where;
    }

    query.where.client_id = app.selectedClient;        // restrict to client

    // Date range
    query.where.comment_date = {
      $gte: startDate.toISOString(),
      $lte: endDate.toISOString()
    };
   

    if (needsResolved) {
      query.where.needs_followup = true;
      query.where.$or = [{ is_resolved: false }, { is_resolved: null }];
      query.order = [['due_date', 'DESC']];
    } else {
      // no follow-up filter
      query.order = [['comment_date', 'DESC']];
    }

    
    const providersInclude = { association: 'providers' };

    query.include.push(providersInclude);


    // never call .toISOString on a bad Date
    query.where.comment_date = {
      $gte: safeISOString(this.state.startDate),
      $lte: safeISOString(this.state.endDate, new Date()),
    };
    

    return query;
  }

  /**
 * Build a query that fetches “providers only”.
 *   – Does NOT touch or read this.state.query at all.
 *   – Returns a completely new query object each time it is called.
 */
  buildLocationsOnlyQuery() {
    const { app } = this.props;
    const { needsResolved, startDate, endDate, locations } = this.state;

    const query =  {
      distinct: true, // this keeps counts accurate with multiple rows
      where: {},
      include: [
        {
          association: 'locations',
          attributes: ['id', 'name', 'address1', 'address2', 'city', 'state', 'postal_code'],
        }
      ],
      // order set in buildQuery
      limit: 500,
      offset: 0,
    };
    

    if (locations && locations.length > 0) {
      query.include[0].where = {
        id: locations.map((l) => l.id),
      }
    } else if (query.include[0].where) {
      delete query.include[0].where;
    }

    query.where.client_id = app.selectedClient;        // restrict to client

    // Date range
    query.where.comment_date = {
      $gte: startDate.toISOString(),
      $lte: endDate.toISOString()
    };
   

    if (needsResolved) {
      query.where.needs_followup = true;
      query.where.$or = [{ is_resolved: false }, { is_resolved: null }];
      query.order = [['due_date', 'DESC']];
    } else {
      // no follow-up filter
      query.order = [['comment_date', 'DESC']];
    }

    
    const locationsInclude = { association: 'locations' };

    query.include.push(locationsInclude);


    // never call .toISOString on a bad Date
    query.where.comment_date = {
      $gte: safeISOString(this.state.startDate),
      $lte: safeISOString(this.state.endDate, new Date()),
    };
    

    return query;
  }

  fetchDataWithPagingReset() {
    this.setState(
      { query: { ...this.state.query, offset: 0 } },
      this.fetchData,
    );
  }

  fetchData = () => {          // ← keeps this no matter who calls it    
    const { dispatch } = this.props;
    // console.log('QUERY: ', query);
    dispatch(queryProviderComments(this.buildQuery(false)));

    let q = {
      order: [
        ['name', 'ASC'],
      ],
      limit: 25,
      offset: 0,
    }

    dispatch(queryClients(q));

    // sub queries
    dispatch(queryProviderCommentsDocuments(this.buildDocsOnlyQuery()));
    dispatch(queryProviderCommentsProviders(this.buildProvidersOnlyQuery()));
    dispatch(queryProviderCommentsContacts(this.buildContactsOnlyQuery()));
    dispatch(queryProviderCommentsLocations(this.buildLocationsOnlyQuery()));

  }

  fetchTagNamespaces() {
    this.props.dispatch(
      listTagNamespacesByClient(this.props.app.selectedClient),
    );
  }

/* -----------------------------------------------------------
 * CSV download – matches the old layout, adds providers /
 * contacts / locations / tag columns, no row-shift issues.
 * -----------------------------------------------------------*/
fetchCSVDownload = async () => {
  const nameSafe = this.props.selectedClientName.replaceAll(' ', '_');
  const filename = `${nameSafe}_doctivity_activity_notes.csv`;

  /* 1 ─ fetch the same five slices you use in fetchData */
  const [
    base, docs, provs, contacts, locs
  ] = await Promise.all([
    axiosInstance.get('/ProviderComments', {
      params: { opts: { ...this.buildQuery(false),  format:'json' } }
    }),
    axiosInstance.get('/ProviderComments', {
      params: { opts: { ...this.buildDocsOnlyQuery(),      format:'json' } }
    }),
    axiosInstance.get('/ProviderComments', {
      params: { opts: { ...this.buildProvidersOnlyQuery(), format:'json' } }
    }),
    axiosInstance.get('/ProviderComments', {
      params: { opts: { ...this.buildContactsOnlyQuery(),  format:'json' } }
    }),
    axiosInstance.get('/ProviderComments', {
      params: { opts: { ...this.buildLocationsOnlyQuery(), format:'json' } }
    })
  ]);

  /* 2 ─ merge like the table */
  const rows = mergeCommentsDocsAndProviders(
    base.data, docs.data, provs.data, contacts.data, locs.data
  );

  /* 3 ─ discover all namespaces that appear (each becomes a column) */
  const namespaceSet = new Map();   // namespace.id → namespace.name
  rows.forEach((r) =>
    (r.tags || []).forEach((t) => namespaceSet.set(t.namespace_id, t.namespace?.name))
  );
  const nsList = Array.from(namespaceSet.values());

  /* 4 ─ helper: quote when needed */
  const csvQ = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  /* 5 ─ static header */
  const header = [
    'Note ID','Doctivity User','Note Date','Needs Resolved','Resolved',
    'Due Date','Resolved Date','Documents','Encounter Type','Activity Note',
    'Providers','Contacts (Company)','Locations',
    ...nsList                                   // tag columns
  ];

  const matrix = [header];

  /* 6 ─ each data row */
  rows.forEach((r) => {
    const docCount    = (r.documents || []).length;
    const provString  = (r.providers || [])
      .map((p) => `${p.first_name} ${p.last_name}${p.credential ? ', ' + p.credential : ''}`)
      .join('\n');
    const contactString = (r.contacts || [])
      .map((c) => `${c.first_name} ${c.last_name}${c.company ? ' (' + c.company + ')' : ''}`)
      .join('\n');
    const locString = (r.locations || [])
      .map((l) => LocationsUtil.formatFullNameAndAddress(l,false))
      .join('\n');

    /* resolved flag / dates */
    const needsRes  = r.needs_followup ? 'Yes' : 'No';
    const resolved  = r.needs_followup ? (r.is_resolved ? 'Yes' : 'No') : '';
    const dueDate   = r.due_date     ? new Date(r.due_date).toLocaleDateString('en-US') : '';
    const resDate   = r.resolved_date? new Date(r.resolved_date).toLocaleDateString('en-US') : '';

    const row = [
      `=HYPERLINK("${document.location.origin}/notes/${r.id}")`,
      csvQ(`${r.user?.last_name ?? ''}, ${r.user?.first_name ?? ''}`),
      csvQ(new Date(r.comment_date).toLocaleDateString('en-US')),
      needsRes, resolved,
      dueDate, resDate,
      docCount ? `Yes (${docCount})` : '',
      r.encounter_type_id ? EncounterTypeUtil.getType(r.encounter_type_id).name : 'Unspecified',
      csvQ(r.body?.replace(/\n/g,' ') || ''),
      csvQ(provString),
      csvQ(contactString),
      csvQ(locString)
    ];

    /* tag columns (namespace order preserved) */
    nsList.forEach((ns) => {
      const tagVals = (r.tags || [])
        .filter((t) => t.namespace?.name === ns)
        .map((t) => t.name)
        .join(', ');
      row.push(csvQ(tagVals));
    });

    matrix.push(row);
  });

  /* 7 ─ download */
  const csvString = CsvUtil.matrixToString(matrix);
  fileDownload(csvString, filename);
};
 
  /*
  getMergedRows() {
    const {
      commentsData,
      documentsData,
      providersData,
      contactsData,
      locationsData
    } = this.props;

    return mergeCommentsDocsAndProviders(
      commentsData,
      documentsData,
      providersData,
      contactsData,
      locationsData,
      this.state.providers.length  > 0,   // require providers?
      this.state.contacts.length   > 0,   // require contacts?
      this.state.locations.length  > 0    // require locations?
    );

  }
*/

  render() {
    const { comments, classes, app} = this.props;

    const {
      query,
      findProviderOpen,
      findContactOpen,
      findUserOpen,
      findEncounterTypeOpen,
      providers,
      locations,
      findLocationOpen,
      contacts,
      encounterTypes,
    } = this.state;

    const users = this.getUsers();

    if (StoreUtil.isSaving(this.props.editComment)) {
      return <LoadingView />;
    }
    
    return (
      <>
        <div className={classes.container}>
          <Card className={classes.card}>
            <CardContent> 
              <div className={classes.tableHeader}>
                <Typography component='h2' variant='h6' color='primary'>
                  All Activity Notes
                </Typography>
                <div className={classes.pdfButton}>
                  <IconButton
                    onClick={() => {
                      this.fetchCSVDownload();
                    }}
                    size='large'
                  >
                    <GetApp className={classes.pdfIcon} />
                  </IconButton>
                </div>
              </div>

              <div className={classes.filters}>
                <FormControl className={classes.rangeControl}>
                  <DatePicker
                    renderInput={(params) => <TextField {...params} />}
                    label='From'
                    name='from'
                    value={this.state.startDate}
                    views={['month', 'day']}
                    maxDate={this.state.endDate}
                    onChange={this.onChangeStartDate}
                    format={DateUtil.LDML_DISPLAY_FORMAT}
                  />
                </FormControl>
                <div className={classes.filterSpace} />
                <FormControl className={classes.rangeControl}>
                  <DatePicker
                    renderInput={(params) => <TextField {...params} />}
                    label='To'
                    value={this.state.endDate}
                    views={['month', 'day']}
                    minDate={this.state.startDate}
                    maxDate={new Date()}
                    onChange={this.onChangeEndDate}
                    name='to'
                    format={DateUtil.LDML_DISPLAY_FORMAT}
                  />
                </FormControl>
                <div className={classes.filterSpace} />
                <div className={classes.filterSpace} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={this.state.needsResolved}
                      onChange={this.onNeedsResolvedChange}
                    />
                  }
                  label='Needs Resolved'
                />
              </div>
              <div className={classes.filters}>
                {locations.map((p) => (
                  <Chip
                    key={`locations-${p.id}`}
                    label={`${p.name ?? ''} ${p.address1}`}
                    onDelete={() => {
                      this.onDeleteLocation(p);
                    }}
                    variant='outlined'
                  />
                ))}
                {!locations.length && (
                  <SmallIconTextButton
                    key='by-location'
                    onClick={this.onAddLocation}
                    icon='filter_list'
                    text='By Location'
                  />
                )}
                {providers.map((p) => (
                  <Chip
                    key={`prpvider-${p.id}`}
                    label={`${p.last_name}, ${p.first_name}`}
                    onDelete={() => {
                      this.onDeleteProvider(p);
                    }}
                    variant='outlined'
                  />
                ))}
                {!providers.length && (
                  <SmallIconTextButton
                    key='by-provider'
                    onClick={this.onAddProvider}
                    icon='filter_list'
                    text='By Provider'
                  />
                )}
                <div className={classes.filterSpace} />
                {contacts.map((c) => (
                  <Chip
                    key={`contact-${c.id}`}
                    label={`${c.last_name}, ${c.first_name}`}
                    onDelete={() => {
                      this.onDeleteContact(c);
                    }}
                    variant='outlined'
                  />
                ))}
                {!contacts.length && (
                  <SmallIconTextButton
                    key='by-contact'
                    onClick={this.onAddContact}
                    icon='filter_list'
                    text='By Contacts'
                  />
                )}
                <div className={classes.filterSpace} />
                <SmallIconTextButton
                  key='by-encounter-type'
                  onClick={this.onAddEncounterType}
                  icon='filter_list'
                  text='By Encounter Type'
                />
                {encounterTypes?.length > 0 && <Chip
                  label={encounterTypes.map((s) => s.name).join(', ')}
                  onDelete={() => {
                    this.onDeleteEncounterType('all');
                  }}
                  variant='outlined'
                />}
                <div className={classes.filterSpace} />
                {users.map((u) => (
                  <Chip
                    key={`user-${u.id}`}
                    label={`${u.last_name}, ${u.first_name}`}
                    onDelete={() => {
                      this.onDeleteUser(u);
                    }}
                    variant='outlined'
                  />
                ))}
                {!users.length && (
                  <SmallIconTextButton
                    key='by-user'
                    onClick={this.onAddUser}
                    icon='filter_list'
                    text='By User'
                  />
                )}
                <div className={classes.filterSpace} />
                {this.state.tags.map((namespace) => (
                  <Chip
                    key={`tag-${namespace.id}`}
                    label={`${namespace.name}: ${namespace.tags.map((t) => t.name).join(', ')}`}
                    onDelete={() => {
                      this.onDeleteTag(namespace);
                    }}
                    variant='outlined'
                  />
                ))}
                {!StoreUtil.isLoaded(this.props.tagNamespaces) ? (
                  <LoadingView />
                ) : (
                  StoreUtil.getData(this.props.tagNamespaces).map(
                    (namespace) => (
                      namespace.activity_notes_drop_down && namespace.tags.length > 0 && (
                      <SmallIconTextButton
                        key={`by-namespace-${namespace.id}`}
                        onClick={(event) => {
                          this.onAddTagNamespace(event, namespace);
                        }}
                        icon='filter_list'
                        text={`By ${namespace.name}`}
                      />
                      )
                    ),
                  )
                )}
              </div>

              <DynamicTable
                columns={columns} // providers.length > 0 ? columns.slice(1) :
                data={comments}
                showFilters={false}
                ContainerType={React.Fragment}
                query={query}
                onQueryChange={this.onQueryChange}
                getRowHref={this.getRowHref}
                onRowClick={this.onRowClick}
                rowClasses={classes}
              />
              {findLocationOpen && (
                <FindLocationDialog
                  clientId={app.selectedClient}
                  open={findLocationOpen}
                  onSelect={this.onLocationSelected}
                  onClose={this.onLocationSelected}
                />
              )}
              {findProviderOpen && (
                <FindProviderDialog
                  clientId={app.selectedClient}
                  open={findProviderOpen}
                  onSelect={this.onProviderSelected}
                />
              )}
              {findContactOpen && (
                <FindContactDialog
                  clientId={app.selectedClient}
                  open={findContactOpen}
                  onSelect={this.onContactSelected}
                />
              )}
              {findUserOpen && (
                <FindUserDialog
                  clientId={app.selectedClient}
                  open={findUserOpen}
                  onSelect={this.onUserSelected}
                />
              )}
              {findEncounterTypeOpen && (
                <Menu
                  anchorEl={this.state.encounterTypeAnchorEl}
                  onClose={() => this.onEncounterTypeSelected(null)}
                  open={findEncounterTypeOpen}
                >
                  {EncounterTypeUtil.getAll().map((s) => (
                    <MenuItem
                      key={s.id}
                      onClick={() => this.onEncounterTypeSelected(s)}
                    >
                      {this.state.encounterTypes.find(
                        (et) => et.id === s.id,
                      ) ? (
                        <ListItemIcon>
                          <Check />
                        </ListItemIcon>
                      ) : null}
                      {s.name}
                    </MenuItem>
                  ))}
                </Menu>
              )}
              {this.state.tagsOpen && this.state.tagsAnchorEl && (
                <Menu
                  anchorEl={this.state.tagsAnchorEl}
                  onClose={() => this.onTagSelected(null)}
                  open={this.state.tagsOpen}
                >
                  {this.state.tagsNamespace.tags.map((t) => (
                    <MenuItem key={t.id} onClick={() => this.onTagSelected(t)}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  getUsers() {
    if (this.state.users) {
      return this.state.users;
    }

    if (!this.props.user.is_admin) {
      return [this.props.user];
    }
    return [];//[this.props.user];
  }

  onQueryChange = (query) => {
    this.setState({ query }, this.fetchData);
  };

  getRowHref = (row) => {
    return `/notes/${row.id}`;
  };
  onRowClick = (row) => {
    this.props.router.navigate(`/notes/${row.id}`);
  };

  onAddProvider = () => {
    this.setState({
      findProviderOpen: true,
    });
  };

  onAddLocation = () => {
    this.setState({
      findLocationOpen: true,
    });
  };

  onLocationSelected = (location) => {
    if (!location) {
      this.setState({
        findLocationOpen: false,
      });
    } else {
      this.setState((state) => {
        let existing = false;
        const newLocations = [...state.locations];
        newLocations.forEach((l) => {
          if (l.id === location.id) {
            existing = true;
          }
        });
        if (!existing) {
          newLocations.push(location);
        }
        return {
          locations: newLocations,
          findLocationOpen: false,
        };
      }, this.fetchDataWithPagingReset);
    }
  };
  onProviderSelected = (provider) => {
    if (!provider) {
      // cancel sends a null provider, just close
      this.setState({
        findProviderOpen: false,
      });
    } else {
      this.setState((state) => {
        let existing = false;
        const newProviders = [...state.providers];
        newProviders.forEach((p) => {
          if (p.id === provider.id) {
            existing = true;
          }
        });
        if (!existing) {
          newProviders.push(provider);
        }
        return {
          providers: newProviders,
          findProviderOpen: false,
        };
      }, this.fetchDataWithPagingReset);
    }
  };

  onDeleteLocation = (location) => {
    this.setState((state) => {
      const newLocations = [...state.locations];
      if (newLocations.length > 0) {
        const i = newLocations.indexOf(location);
        if (i >= 0) {
          newLocations.splice(i, 1);
        }
      }
      return { locations: newLocations };
    }, this.fetchDataWithPagingReset);
  };

  onDeleteProvider = (provider) => {
    this.setState((state) => {
      const newProviders = [...state.providers];
      if (newProviders.length > 0) {
        const i = newProviders.indexOf(provider);
        if (i >= 0) {
          newProviders.splice(i, 1);
        }
      }
      return { providers: newProviders };
    }, this.fetchDataWithPagingReset);
  };

  onAddContact = () => {
    this.setState({
      findContactOpen: true,
    });
  };

  onContactSelected = (contact) => {
    if (!contact) {
      // cancel sends a null provider, just close
      this.setState({
        findContactOpen: false,
      });
    } else {
      this.setState((state) => {
        let existing = false;
        const newContacts = [...state.contacts];
        newContacts.forEach((c) => {
          if (c.id === contact.id) {
            existing = true;
          }
        });
        if (!existing) {
          newContacts.push(contact);
        }
        return {
          contacts: newContacts,
          findContactOpen: false,
        };
      }, this.fetchDataWithPagingReset);
    }
  };

  onDeleteContact = (contact) => {
    this.setState((state) => {
      const newContacts = [...state.contacts];
      if (state.contacts.length > 0) {
        const i = state.contacts.indexOf(contact);
        if (i >= 0) {
          newContacts.splice(i, 1);
        }
      }
      return { contacts: newContacts };
    }, this.fetchDataWithPagingReset);
  };

  onAddUser = () => {
    this.setState({
      findUserOpen: true,
    });
  };

  onUserSelected = (user) => {
    if (!user) {
      // cancel sends a null user, just close
      this.setState({
        findUserOpen: false,
      });
    } else {
      const users = [...this.getUsers()];
      // eslint-disable-next-line no-unused-vars
      this.setState((_state) => {
        let existing = false;
        users.forEach((u) => {
          if (u.id === user.id) {
            existing = true;
          }
        });
        if (!existing) {
          users.push(user);
        }
        return {
          users,
          findUserOpen: false,
        };
      }, this.fetchDataWithPagingReset);
    }
  };

  onDeleteUser = (user) => {
    // eslint-disable-next-line no-unused-vars
    this.setState((_state) => {
      const users = [...this.getUsers()];
      if (users.length > 0) {
        const i = users.indexOf(user);
        if (i >= 0) {
          users.splice(i, 1);
        }
      }
      return { users };
    }, this.fetchDataWithPagingReset);
  };

  onAddTagNamespace = (event, namespace) => {
    this.setState({
      tagsOpen: true,
      tagsAnchorEl: event.target,
      tagsNamespace: namespace,
    });
  };

  onTagSelected = (tag) => {
    if (!tag) {
      this.setState({
        tagsOpen: false,
        tagsAnchorEl: null,
        tagsNamespace: null,
      });
    } else {
      this.setState((state) => {
        console.log(state.tagsNamespace);
        const newTags = [...state.tags];
        let namespace = newTags.find((n) => state.tagsNamespace.id === n.id);
        if (!namespace) {
          namespace = {
            ...state.tagsNamespace,
            tags: [tag],
          };
          newTags.push(namespace);
        } else {
          let existingTag = namespace.tags.find((t) => t.id === tag.id);
          if (!existingTag) {
            if (!namespace.activity_notes_multiple && namespace.tags > 0) {
              namespace.tags = [];
            }
            namespace.tags = [...namespace.tags, tag];
          } // if its already in there, do nothing
        }
        return {
          tags: newTags,
          tagsOpen: false,
          tagsAnchorEl: null,
          tagsNamespace: null,
        };
      }, this.fetchDataWithPagingReset);
    }
  };

  onDeleteTag = (tag) => {
    this.setState((state) => {
      if (state.tags.length > 0) {
        const i = state.tags.indexOf(tag);
        if (i >= 0) {
          state.tags.splice(i, 1);
        }
      }
      return { tags: state.tags };
    }, this.fetchDataWithPagingReset);
  };

  onAddEncounterType = (event) => {
    this.setState({
      findEncounterTypeOpen: true,
      encounterTypeAnchorEl: event.target,
    });
  };

  onEncounterTypeSelected = (encounterType) => {
    if (!encounterType) {
      this.setState({
        encounterTypeAnchorEl: null,
        findEncounterTypeOpen: false,
      });
    } else {
      this.setState((state) => {
        let existing = false;
        state.encounterTypes.forEach((s) => {
          if (s.id === encounterType.id) {
            existing = true;
          }
        });
        const newEncounterTypes = state.encounterTypes.filter(
          (c) => c.id !== encounterType.id,
        );
        if (!existing) {
          newEncounterTypes.push(encounterType);
        }
        return {
          encounterTypes: newEncounterTypes,
          findEncounterTypeOpen: false,
          encounterTypeAnchorEl: null,
        };
      }, this.fetchDataWithPagingReset);
    }
  };

  onDeleteEncounterType = (encounterType) => {
    this.setState((state) => {
      if (encounterType === 'all') {
        return { encounterTypes: [] };
      }
      const newEncounterTypes = [...state.encounterTypes];
      if (state.encounterTypes.length > 0) {
        const i = state.encounterTypes.indexOf(encounterType);
        if (i >= 0) {
          newEncounterTypes.splice(i, 1);
        }
      }
      return { encounterTypes: newEncounterTypes };
    }, this.fetchDataWithPagingReset);
  };

  onNeedsResolvedChange = (event) => {
    if (event.target.checked) {
      columns[1] = {
        label: 'Due Date',
        key: 'due_date',
        type: 'date',
        format: DateUtil.formatDateForUser,
        style: { width: 60 },
        hideForMobile: false,
      };
    } else {
      columns[1] = {
        label: 'Date',
        key: 'comment_date',
        type: 'date',
        format: DateUtil.formatDateForUser,
        style: { width: 60 },
        hideForMobile: false,
      };
    }

    this.setState(
      {
        needsResolved: event.target.checked,
      },
      this.fetchDataWithPagingReset,
    );
  };

  onChangeStartDate = (newDate) => {
     if (!isValidDate(newDate)) {
      return;
    }
    
    sessionStorage.setItem('startDate', newDate);
    this.setState(
      {
        startDate: startOfDay(newDate),
      },
      this.fetchDataWithPagingReset,
    );
  };

  onChangeEndDate = (newDate) => {
     if (!isValidDate(newDate)) {
      return;
    }
    sessionStorage.setItem('endDate', newDate);
    this.setState(
      {
        endDate: endOfDay(newDate),
      },
      this.fetchDataWithPagingReset,
    );
  };
}

ProviderCommentsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  app: PropTypes.object,
  clients: PropTypes.object,  
  clientName: PropTypes.object,
  selectedClient: PropTypes.object,
  selectedClientName: PropTypes.object,
  user: PropTypes.object,
  comments: PropTypes.object.isRequired,
  editComment: PropTypes.object,
  tagNamespaces: PropTypes.object,
  commentsData  : PropTypes.object,
  documentsData : PropTypes.object,
  providersData : PropTypes.object,
  contactsData  : PropTypes.object,
  locationsData : PropTypes.object

};


function mapStateToProps(state) {
  const { app, user, clients } = state;

  const clientsData = StoreUtil.get(clients, StoreUtil.COMMON_LIST);
  const selectedClient = app.selectedClient
    ? app.selectedClient
    : StoreUtil.getData(clientsData)[0].id;
  const selectedClientName = clientsData.data.find((c) => c.id === selectedClient).name;

  const commentsTable = StoreUtil.get(state.providerComments, StoreUtil.COMMON_TABLE); // your main comments
  //const documentsTable = StoreUtil.get(state.providerCommentsDocuments, StoreUtil.COMMON_TABLE); // the documents-for-comments
  //const providersTable = StoreUtil.get(state.providerCommentsProviders, StoreUtil.COMMON_TABLE); // the documents-for-comments
  //const contactsTable = StoreUtil.get(state.providerCommentsContacts, StoreUtil.COMMON_TABLE); // the documents-for-comments

  const extrasSlice = state.providerCommentsExtras || {};

  /* each leaf reducer lives one level deeper */
  const documentsTable = StoreUtil.get(
    extrasSlice.documents, StoreUtil.COMMON_TABLE);

  const providersTable = StoreUtil.get(
    extrasSlice.providers, StoreUtil.COMMON_TABLE);

  const contactsTable  = StoreUtil.get(
    extrasSlice.contacts,  StoreUtil.COMMON_TABLE);

  const locationsTable  = StoreUtil.get(
    extrasSlice.locations,  StoreUtil.COMMON_TABLE);

  const comments = StoreUtil.getData(commentsTable) || { count: 0, rows: [] };
  
  // ------------ do the merge *once* and pass it down ----------------------
  const mergedRows = mergeCommentsDocsAndProviders(
    StoreUtil.getData(commentsTable) || { rows: [] },
    StoreUtil.getData(documentsTable) || [],
    StoreUtil.getData(providersTable) || [],
    StoreUtil.getData(contactsTable) || [],
    StoreUtil.getData(locationsTable) || [],
  );



 /*
  function mergeCommentsAndDocuments(comments, docs) {
  const documentsList = Array.isArray(docs) ? docs : (docs?.rows || []);
  const docMap = {};
  for (const item of documentsList) {
    if (item && item.id != null && Array.isArray(item.documents)) {
      docMap[item.id] = item.documents;
    }
  }
  return (comments.rows || []).map((c) => ({
    ...c,
    documents: docMap[c.id] || []
  }));
}

/*
  let providersFlag = (providers && providers.length > 0) ? true : false;
  let contactsFlag = (contacts && contacts.length > 0) ? true : false
  let locationsFlag = (locations && locations.length > 0) ? true : false ;
*/

  


  //  const mergedRows = mergeCommentsAndDocuments(comments, documentsData);

  return {
    app,
    user,
   comments: {
      ...comments,
      rows: mergedRows,
    },
    //comments: StoreUtil.get(providerComments, StoreUtil.COMMON_TABLE),
    //clients: StoreUtil.get(state.clients, StoreUtil.COMMON_TABLE),
    selectedClientName,
    editComment: StoreUtil.get(state.providerComments, StoreUtil.COMMON_EDIT_ITEM),
     commentsData  : StoreUtil.getData(commentsTable)  || { count: 0, rows: [] },
    documentsData : StoreUtil.getData(documentsTable) || [],
    providersData : StoreUtil.getData(providersTable) || [],
    contactsData  : StoreUtil.getData(contactsTable)  || [],
    locationsData : StoreUtil.getData(locationsTable) || [],
    tagNamespaces: StoreUtil.get(
      state.tagNamespaces,
      API_TAG_NAMESPACES_LIST_BY_CLIENT,
    ),

  };
}

const styled = withStyles(styles)(ProviderCommentsPage);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as ProviderCommentsPage };
